<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useOrganizerFeeDisplay } from '@/composables/useOrganizerFeeDisplay'

const { t } = useI18n()
const { formattedCreditRate } = useOrganizerFeeDisplay()

const features = [
  { key: 'editions', icon: 'M6 8h28M6 14h28M6 20h20', path2: '' },
  { key: 'forms', icon: 'M10 6h20v4H10zM10 16h20v4H10zM10 26h12v4H10z', path2: '' },
  { key: 'waiting', icon: 'M20 6v8l5 3M20 34a14 14 0 100-28 14 14 0 000 28z', path2: '' },
  { key: 'cancellation', icon: 'M8 8l24 24M32 8L8 32', path2: '' },
  { key: 'switch', icon: 'M8 20h24M26 14l6 6-6 6M14 14l-6 6 6 6', path2: '' },
  { key: 'vouchers', icon: 'M6 14h28v12H6zM14 14V8M26 14V8M14 26v6M26 26v6', path2: '' },
  { key: 'team', icon: 'M14 18a5 5 0 100-10 5 5 0 000 10zM26 18a5 5 0 100-10 5 5 0 000 10zM6 34c0-4.418 3.582-8 8-8h12c4.418 0 8 3.582 8 8', path2: '' },
  { key: 'newsletters', icon: 'M6 10h28v20H6zM6 10l14 12L34 10', path2: '' },
  { key: 'volunteers', icon: 'M20 6l3 9h9l-7.5 5.5 2.9 8.9L20 24l-7.4 5.4 2.9-8.9L8 15h9z', path2: '' },
  { key: 'route', icon: 'M8 32c4-8 8-4 12-12s8-4 12-12', path2: '' },
  { key: 'aid', icon: 'M20 8v24M8 20h24', path2: '' },
  { key: 'gear', icon: 'M14 8h12l4 8H10zM10 16h20v16H10zM16 24h8', path2: '' },
  { key: 'programme', icon: 'M8 8h24v4H8zM8 16h24v4H8zM8 24h16v4H8z', path2: '' },
  { key: 'sponsors', icon: 'M20 6l4 8 9 1.3-6.5 6.3 1.5 9L20 26l-8 4.2 1.5-9L7 14.7l9-1.3z', path2: '' },
  { key: 'practical', icon: 'M20 4C13.373 4 8 9.373 8 16c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12zm0 16a4 4 0 110-8 4 4 0 010 8z', path2: '' },
] as const

// Only the "newsletters" body embeds the configured email-credit rate - the
// rest render without interpolation params.
function itemBodyParams(key: string): Record<string, string> {
  return key === 'newsletters' ? { rate: formattedCreditRate.value } : {}
}
</script>

<template>
  <section class="bg-slate-50 py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-2xl">
        <p class="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
          {{ t('organizers_lp.feature_grid.label') }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
          {{ t('organizers_lp.feature_grid.heading') }}
        </h2>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        <div
          v-for="feature in features"
          :key="feature.key"
          class="bg-white rounded-xl p-5 border border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-8 h-8 text-emerald-500 mt-0.5">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full">
                <path :d="feature.icon" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div>
              <h3 class="text-sm font-bold text-slate-900">{{ t(`organizers_lp.feature_grid.item_${feature.key}`) }}</h3>
              <p class="text-xs text-slate-500 mt-1 leading-relaxed">{{ t(`organizers_lp.feature_grid.item_${feature.key}_body`, itemBodyParams(feature.key)) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
